/**
 * Analytics Portal V1 — the Summary metric map (Phase 10, plan §4.2).
 *
 * Every entry names a key the Phase 8 Summary payload may contain. Nothing here computes,
 * sums or derives a value: a metric is rendered only when `kpis[key]` exists, and `types`
 * limits a metric to scopes that can actually produce it (the server's `counts_by_type`),
 * so a Personal-only selection never shows an Event or Capital card (C4 spirit).
 */
export const SUMMARY_HEADLINE = [
  { key: "unique_audience", label: "Unique audience",
    hint: "One person counted once across everything in scope." },
  { key: "visits", label: "Total entity visits",
    hint: "One visit per entity per session." },
  { key: "engagement_actions", label: "Total engagement",
    hint: "Qualifying engagement events only." },
];

const PROFILE_TYPES = ["personal", "business", "capital"];

export const SUMMARY_GROUPS = [
  {
    id: "discovery",
    title: "Discovery",
    description: "How people came across the profiles, events and resources you manage.",
    metrics: [
      { key: "search_impressions", label: "Search impressions" },
      { key: "search_clicks", label: "Search clicks" },
      { key: "directory_impressions", label: "Directory impressions" },
      { key: "directory_clicks", label: "Directory clicks" },
    ],
  },
  {
    id: "growth",
    title: "Audience growth",
    description: "Followers, fans and bookmarks gained and lost in this period.",
    metrics: [
      { key: "followers_added", label: "Followers gained", types: PROFILE_TYPES },
      { key: "fans_added", label: "Fans gained", types: PROFILE_TYPES },
      { key: "bookmarks_added", label: "Bookmarks added" },
      { key: "followers_lost", label: "Followers lost", types: PROFILE_TYPES },
      { key: "fans_lost", label: "Fans lost", types: PROFILE_TYPES },
      { key: "bookmarks_removed", label: "Bookmarks removed" },
    ],
  },
  {
    id: "interest",
    title: "Inbound interest",
    description: "What people asked for and acted on.",
    metrics: [
      { key: "questions", label: "Questions received" },
      { key: "meeting_requests", label: "Meetings requested", types: PROFILE_TYPES },
      { key: "cta_clicks", label: "Event CTA clicks", types: ["event"] },
      { key: "pitch_decks", label: "Pitch decks submitted", types: ["capital"] },
      { key: "visit_clicks", label: "Resource visits", types: ["resource"] },
    ],
  },
  {
    id: "sharing",
    title: "Share actions",
    description: "Copy Link and Share with Connections, reported separately (Decision 7). "
      + "Visiting a resource is not a share action.",
    metrics: [
      { key: "copy_link", label: "Copy link", types: ["resource"] },
      { key: "shared_with_connections", label: "Shared with connections", types: ["resource"] },
    ],
  },
];

/** The entity types the server says are actually in scope for this Summary request. */
export const scopeTypesOf = (payload) => Object.entries(payload?.scope?.counts_by_type || {})
  .filter(([, count]) => count > 0)
  .map(([type]) => type);
